import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Account } from '@trading-app/shared';
import { accountsService } from '../../services/accounts';
import { RemoveAccountDialog } from './RemoveAccountDialog';
import { ModeBadge } from '../ModeBadge';

interface Position {
  symbol: string;
  quantity: number;
  marketValue: number;
  costBasis: number;
  gainLoss: number;
  gainLossPercent: number;
  accountId: string;
  accountName: string;
  mode?: 'ERIC' | 'ANNIE' | null;
}

interface HoldingsTableProps {
  accounts: Account[];
  positions: Position[];
  isLoading?: boolean;
}

export function HoldingsTable({ accounts, positions, isLoading = false }: HoldingsTableProps) {
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
  const [accountToRemove, setAccountToRemove] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Mutation to remove account
  const removeMutation = useMutation({
    mutationFn: (accountId: string) => accountsService.removeAccount(accountId),
    onSuccess: () => {
      setAccountToRemove(null);
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
    onError: (error) => {
      console.error('Failed to remove account:', error);
      alert('Failed to remove account. Please try again.');
    },
  });

  const displayPositions = positions;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  // Group positions by account
  const positionsByAccount = displayPositions.reduce((acc, position) => {
    if (!acc[position.accountId]) {
      acc[position.accountId] = [];
    }
    acc[position.accountId].push(position);
    return acc;
  }, {} as Record<string, Position[]>);

  const totalValue = displayPositions.reduce((sum, pos) => sum + pos.marketValue, 0);
  const totalGainLoss = displayPositions.reduce((sum, pos) => sum + pos.gainLoss, 0);
  const totalGainLossPercent = totalGainLoss / (totalValue - totalGainLoss) * 100;

  if (isLoading) {
    return (
      <div className="rh-card p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Your Holdings
        </h2>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rh-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Your Holdings
        </h2>
        <div className="text-right">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Value</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {formatCurrency(totalValue)}
          </div>
          <div className={`text-sm font-medium ${
            totalGainLoss >= 0 ? 'rh-text-positive' : 'rh-text-negative'
          }`}>
            {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss)} ({formatPercent(totalGainLossPercent)})
          </div>
        </div>
      </div>

      {displayPositions.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">No holdings found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(positionsByAccount).map(([accountId, accountPositions]) => {
            const account = accounts.find(acc => acc.id === accountId);
            const isExpanded = expandedAccount === accountId;
            
            return (
              <div key={accountId} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                <button
                  onClick={() => setExpandedAccount(isExpanded ? null : accountId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-brand-primary">
                        {account?.accountName?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {account?.accountName || 'Unknown Account'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {accountPositions.length} positions
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(accountPositions.reduce((sum, pos) => sum + pos.marketValue, 0))}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAccountToRemove(accountId);
                      }}
                      className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      title="Remove account"
                    >
                      <svg 
                        className="w-4 h-4" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" 
                        />
                      </svg>
                    </button>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    {accountPositions.map((position) => {
                      const positionMode = position.mode ?? null;
                      return (
                        <div key={`${accountId}-${position.symbol}`} className="p-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                                <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                                  {position.symbol.slice(0, 2)}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <div className="font-semibold text-gray-900 dark:text-white">
                                    {position.symbol}
                                  </div>
                                  <ModeBadge mode={positionMode} variant="compact" />
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {position.quantity} shares
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900 dark:text-white">
                                {formatCurrency(position.marketValue)}
                              </div>
                              <div className={`text-sm font-medium ${
                                position.gainLoss >= 0 ? 'rh-text-positive' : 'rh-text-negative'
                              }`}>
                                {position.gainLoss >= 0 ? '+' : ''}{formatCurrency(position.gainLoss)}
                                <span className="ml-1">({formatPercent(position.gainLossPercent)})</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Remove Account Dialog */}
      <RemoveAccountDialog
        isOpen={!!accountToRemove}
        onClose={() => setAccountToRemove(null)}
        onConfirm={() => {
          if (accountToRemove) {
            removeMutation.mutate(accountToRemove);
          }
        }}
        accountName={
          accounts.find((acc) => acc.id === accountToRemove)?.accountName || 'Unknown'
        }
        isRemoving={removeMutation.isPending}
      />
    </div>
  );
}
